/**
 * Mechanical edit-turn completeness — shared by onFinish and stream continuation.
 * Prevents finalizing a multi-round incremental edit after the first typechecking patch.
 */

import { lastAssistantHasPendingClientTools } from './lastAssistantHasPendingClientTools';
import { isBuildToolResultFailure } from './prototyperBuildPhases';

export type EditLoopToolStep = {
  toolName: string;
  isComplete: boolean;
  result?: string;
  args?: Record<string, unknown>;
};

export type EditLoopCompletenessInput = {
  messages: readonly unknown[];
  toolSteps: readonly EditLoopToolStep[];
  /** Paths from planComplete.files — empty on cold generate or non-edit turns. */
  plannedFilePaths: readonly string[];
  toolResultContinuationPending: boolean;
  clientToolsBusy?: boolean;
};

const FILE_MUTATION_TOOLS = new Set(['writeFile', 'searchReplace', 'deleteFile']);

export function normalizePlannedPath(path: string): string {
  return path.trim().replace(/^[/\\]+/, '').replace(/\\/g, '/');
}

/** Paths completed via writeFile / searchReplace / deleteFile this turn. */
export function pathsTouchedThisTurn(toolSteps: readonly EditLoopToolStep[]): Set<string> {
  const touched = new Set<string>();
  for (const step of toolSteps) {
    if (!step.isComplete || !FILE_MUTATION_TOOLS.has(step.toolName)) continue;
    const path = step.args?.path;
    if (typeof path === 'string' && path.trim()) {
      touched.add(normalizePlannedPath(path));
    }
  }
  return touched;
}

export function remainingPlannedPaths(
  plannedFilePaths: readonly string[],
  toolSteps: readonly EditLoopToolStep[],
): string[] {
  if (plannedFilePaths.length === 0) return [];
  const touched = pathsTouchedThisTurn(toolSteps);
  return plannedFilePaths
    .map(normalizePlannedPath)
    .filter((p) => p.length > 0 && !touched.has(p));
}

export function hasSuccessfulBuildToolResult(toolSteps: readonly EditLoopToolStep[]): boolean {
  return toolSteps.some(
    (s) =>
      s.toolName === 'build' &&
      s.isComplete &&
      typeof s.result === 'string' &&
      !isBuildToolResultFailure(s.result),
  );
}

export function hasFileChangesThisTurn(toolSteps: readonly EditLoopToolStep[]): boolean {
  return toolSteps.some(
    (s) => FILE_MUTATION_TOOLS.has(s.toolName) && s.isComplete,
  );
}

export function hasBuildToolStep(toolSteps: readonly EditLoopToolStep[]): boolean {
  return toolSteps.some((s) => s.toolName === 'build');
}

/** Continuation budget scales with planned file count (one client tool per POST is common). */
export function computeMaxToolContinuations(plannedFileCount: number): number {
  const scaled = plannedFileCount * 2 + 4;
  return Math.min(24, Math.max(12, scaled));
}

/**
 * True while the edit turn still needs more client-tool rounds or a model build.
 */
export function isEditTurnUnfinished(input: EditLoopCompletenessInput): boolean {
  if (lastAssistantHasPendingClientTools(input.messages)) return true;
  if (input.toolResultContinuationPending) return true;
  if (input.clientToolsBusy) return true;

  if (remainingPlannedPaths(input.plannedFilePaths, input.toolSteps).length > 0) {
    return true;
  }

  if (hasFileChangesThisTurn(input.toolSteps) && !hasSuccessfulBuildToolResult(input.toolSteps)) {
    return true;
  }

  return false;
}

/**
 * Last-resort local compile when the model wrote/patched everything planned but forgot `build`.
 */
export function allowOpportunisticBuild(input: EditLoopCompletenessInput): boolean {
  if (lastAssistantHasPendingClientTools(input.messages)) return false;
  if (input.toolResultContinuationPending) return false;
  if (input.clientToolsBusy) return false;
  if (remainingPlannedPaths(input.plannedFilePaths, input.toolSteps).length > 0) return false;
  if (!hasFileChangesThisTurn(input.toolSteps)) return false;
  if (hasSuccessfulBuildToolResult(input.toolSteps)) return false;
  if (hasBuildToolStep(input.toolSteps)) return false;
  return true;
}

/** Extract normalized paths from a planComplete annotation payload. */
export function plannedPathsFromPlanComplete(plan: Record<string, unknown>): string[] {
  const files = Array.isArray(plan.files) ? plan.files : [];
  return files
    .map((f) => {
      if (f && typeof f === 'object' && typeof (f as { path?: string }).path === 'string') {
        return normalizePlannedPath((f as { path: string }).path);
      }
      return '';
    })
    .filter((p) => p.length > 0);
}

/** Stream should POST another round after tools settle (edit plan or build still open). */
export function shouldContinueEditStream(input: EditLoopCompletenessInput): boolean {
  if (input.toolResultContinuationPending) return true;
  if (lastAssistantHasPendingClientTools(input.messages)) return false;
  if (input.clientToolsBusy) return false;

  if (remainingPlannedPaths(input.plannedFilePaths, input.toolSteps).length > 0) {
    return true;
  }

  if (
    hasFileChangesThisTurn(input.toolSteps) &&
    !hasSuccessfulBuildToolResult(input.toolSteps) &&
    !hasBuildToolStep(input.toolSteps)
  ) {
    return true;
  }

  return false;
}
